import React, { Fragment, useState, useMemo } from 'react';
import { always, complement, cond, isEmpty, pipe, prop, propEq, sortBy, find } from 'ramda';
import { useIntl } from 'gatsby-plugin-intl';
import { getTarget, getWithPrefix, findByType } from '../../utils';
import { ELEMENTS_TYPES } from '../../utils/constants';
import { CardLeft } from '../cardLeft';
import { Quote } from '../quote';
import {
  Container,
  Side,
  Main,
  PseudoLink,
  Link,
  Tooltip,
  TitleWrapper,
  Title,
  VideoComponent,
  CoverWrapper,
  ClickCover
} from './videoCard.styles';
import { useCardLeft } from '../../hooks/useCardLeft';
import { withPrefix } from 'gatsby';
import { CardContainer, CardToggleButton, CardToggleCloseIcon } from '../cardLeft/cardLeft.styles';
import { useBreakpointsContext } from '../../store/BreakpointsContext';
import { renderWhenTrue } from '../../utils/rendering';
import { Modal } from '../modal';

const anyElementHasValue = (elements) => elements.some(pipe(prop('value'), complement(isEmpty)));

const shouldSideBeRendered = (arr) => {
  if (isEmpty(arr)) return false;
  return arr.some(pipe(prop('elements'), anyElementHasValue));
};

export const VideoCard = ({ elements, isExpandable = false }) => {
  const orderedElements = useMemo(() => sortBy(prop('order'))(elements), [elements]);
  const sideElements = [];

  const [isOpen, setOpen] = useState(false);
  const {value: title} = find(propEq('name', 'Title'))(elements) || {};
  const video = find(propEq('name', 'Youtube URL'))(elements) || {};
  const clickCover = find(propEq('name', 'Image Cover'))(elements) || {};

  const { isDesktop } = useBreakpointsContext();

  const handleRequestClose = () => setOpen(false);
  const intl = useIntl();
  const renderTitle = (_, key) =>
    renderWhenTrue(() => (
      <TitleWrapper key={key}>
        <Title>{intl.formatMessage({ id: title })}</Title>
      </TitleWrapper>
    ))(!!title);

  const renderVideo = (video, key) => (
    <Modal
      key={key}
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      styles={{
        content: {
          height: 'auto',
          width: isDesktop ? '80%' : '100%',
          padding: isDesktop ? '25px' : '0',
        },
      }}
    >
      <VideoComponent dangerouslySetInnerHTML={{ __html: video.html }} />
    </Modal>
  );

  const renderClickCover = ({ url }) => (
    <CoverWrapper>
      <ClickCover src={withPrefix(url)} onClick={() => setOpen(true)} />
    </CoverWrapper>
  );

  const card = useCardLeft();

  orderedElements.forEach(
    cond([
      [
        propEq('name', 'Card'),
        (element) => {
          sideElements.push(element);
        },
      ]
    ])
  );

  const renderCard = ({ elements }, key) => {
    const { value: markdown } = findByType(ELEMENTS_TYPES.MARKDOWN, elements);
    const { value: connection } = findByType(ELEMENTS_TYPES.CONNECTION, elements);

    if (isEmpty(markdown) && isEmpty(connection)) return null;

    return <CardLeft elements={elements} key={key} />;
  };

  const renderSide = ({ value, id }) => {
    return (
      <Fragment key={id}>
        {renderWhenTrue(
          always(
            <CardToggleButton isVisible={card.isVisible} onClick={card.toggleVisibility}>
              <CardToggleCloseIcon />
            </CardToggleButton>
          )
        )(shouldSideBeRendered(value))}
        <CardContainer isVisible={card.isVisible} ref={card.containerRef}>
          {value.map(renderCard)}
        </CardContainer>
      </Fragment>
    );
  };

  return (
    <Container>
      <Side>{sideElements.map(renderSide)}</Side>
      <Main>
        {renderTitle(title)}
        {renderClickCover(clickCover)}
        {renderVideo(video)}
      </Main>
    </Container>
  );
};
